/**
 * The hero (spec §4.1–§4.2, K-1/K-2): the programme introduction on the Belmont photo and the featured
 * events. The postbuild script renders this same component to static HTML with the featured snapshot, so
 * the first slide and every gate link are in the HTML before any script runs; in the browser it renders
 * from the snapshot, then from the 5-minute refresh. Every action is a plain <a> to the gate (K-8).
 * With no events at all, the introduction stands alone.
 */
import { Check, Circle, CircleDashed, CircleDot, List, Pause, X } from "lucide-react";
import { links } from "../lib/gate";
import { useRotation, RotationControls } from "./HeroRotation";
import { LocalTime } from "./LocalTime";

/** Without JavaScript every slide is shown, stacked, and the script-only controls are hidden (K-2). */
const NOSCRIPT_STYLE = "<style>.ev-slides .ev-slide{display:block!important;grid-area:auto!important}.ev-slides{gap:16px}.ev-rotation{display:none!important}</style>";

const GLYPHS = { x: X, check: Check, pause: Pause, dot: CircleDot, half: CircleDashed, list: List, ring: Circle };

export function Hero({ hero }) {
  const slides = hero.slides;
  const rotation = useRotation(slides.length);
  const multi = slides.length > 1;
  return (
    <section className="ev-hero" aria-labelledby="ev-hero-title">
      <picture className="ev-hero-bg">
        <source srcSet="/events-assets/belmont-hero.webp" type="image/webp" />
        <img src="/events-assets/belmont-hero.jpg" alt="" aria-hidden="true" width="1200" height="600" fetchPriority="high" decoding="async" />
      </picture>
      <div className="ev-hero-shade" aria-hidden="true" />
      <div className="ev-hero-glow" aria-hidden="true" />
      <div className="ev-wrap ev-hero-grid">
        <div className="ev-up">
          <p className="ev-eyebrow">KGOB events</p>
          <h1 id="ev-hero-title">Good company. <em>Better decisions.</em></h1>
          <p className="ev-lead">Free seminars, workshops, webinars and client evenings from KGOB CPAs &amp; Advisors, across the Carolinas, Pittsburgh and online. Pick a date, bring a guest, RSVP in under a minute.</p>
          <div className="ev-actions">
            <a className="ev-btn ev-btn-gold ev-btn-lg" href="#ev-list" data-testid="hero-see-all">See all events <span aria-hidden="true">→</span></a>
            {hero.next && (
              <a className="ev-btn ev-btn-ghost ev-btn-lg" href={hero.next.href} data-rsvp={hero.next.event_session_id} data-testid="hero-next-rsvp" aria-label={`${hero.next.label === "RSVP" ? "RSVP to the next event" : "Join the waitlist for the next event"}: ${hero.next.title}`}>
                {hero.next.label === "RSVP" ? "RSVP to the next event" : "Join the waitlist for the next event"} <span aria-hidden="true">→</span>
              </a>
            )}
          </div>
          <div className="ev-quick">
            <a href="#ev-past" data-testid="hero-past">Past events</a>
            <a href={links.my} data-testid="hero-my-rsvps">Manage my RSVPs</a>
            <a href={links.my} data-testid="hero-sign-in">Sign in</a>
          </div>
        </div>
        {slides.length > 0 && (
          <div className="ev-featured" role="region" aria-roledescription={multi ? "carousel" : undefined} aria-label={hero.fallback ? "Next event" : "Featured events"} tabIndex={multi ? 0 : undefined} {...rotation.holdProps}>
            <noscript dangerouslySetInnerHTML={{ __html: NOSCRIPT_STYLE }} />
            <div className="ev-slides" id="ev-featured-slides" data-stale={hero.stale ? "" : undefined}>
              {(slides || []).map((s, i) => <Slide key={s.slug} s={s} first={i === 0} index={i} total={slides.length} active={i === rotation.i} />)}
            </div>
            {multi && <RotationControls titles={(slides || []).map((s) => s.title)} rotation={rotation} />}
          </div>
        )}
      </div>
    </section>
  );
}

const typeStyle = (hue) =>
  hue === undefined || hue === null ? undefined : {
    "--type": `oklch(0.52 0.13 ${hue})`,
    "--type-text": `oklch(0.78 0.12 ${hue})`,
    "--type-deep": `oklch(0.40 0.13 ${hue})`,
    "--type-tint": `oklch(0.52 0.13 ${hue} / 0.14)`,
  };

export function Badge({ badge }) {
  const Glyph = GLYPHS[badge.glyph] || Circle;
  return (
    <span className={`ev-badge ev-badge-${badge.tone}`} data-badge={badge.tone}>
      <Glyph size={13} aria-hidden="true" {...(badge.glyph === "dot" ? { fill: "currentColor" } : {})} />{badge.label}
    </span>
  );
}

export function Slide({ s, first, index, total, active }) {
  const d = s.date;
  const badge = s.cancelled ? { label: "Cancelled", tone: "cancelled", glyph: "x" } : d?.badge ?? null;
  return (
    <article
      className="ev-slide"
      style={typeStyle(s.type?.hue)}
      data-active={active ? "" : undefined}
      aria-hidden={total > 1 && !active ? "true" : undefined}
      aria-roledescription={total > 1 ? "slide" : undefined}
      aria-label={total > 1 ? `${index + 1} of ${total}: ${s.title}` : undefined}
    >
      <div className="ev-slide-card">
        <div className="ev-slide-media">
          {s.image ? (
            <img src={s.image.url} alt={s.image.alt} width={1600} height={900} loading={first ? "eager" : "lazy"} decoding="async" {...(first ? { fetchPriority: "high" } : {})} />
          ) : d ? (
            <div className="ev-date-tile" aria-hidden="true"><span className="ev-wd">{d.tile.weekday}</span><span className="ev-day">{d.tile.day}</span><span className="ev-mo">{d.tile.month}</span></div>
          ) : (
            <div className="ev-date-tile" aria-hidden="true"><span className="ev-mo">Dates to be announced</span></div>
          )}
        </div>
        <div className="ev-slide-body">
          {s.type && <span className="ev-type-tag">{s.type.name}</span>}
          <h2 className="ev-slide-title"><a href={s.viewHref} data-testid={`hero-slide-link-${s.slug}`}>{s.title}</a></h2>
          {s.tagline && <p className="ev-slide-tagline">{s.tagline}</p>}
          <div className="ev-slide-meta">
            {d ? (
              <>
                <span className="ev-when"><time dateTime={d.starts_at}>{d.when}</time></span>
                {s.local && <LocalTime local={s.local} />}
                <span>{d.place}</span>
              </>
            ) : (
              <span>{s.cancelled ? "This event has been cancelled." : "New dates will be announced soon."}</span>
            )}
          </div>
          {badge && <Badge badge={badge} />}
          <div className="ev-slide-actions">
            <a className="ev-btn ev-btn-ghost" href={s.viewHref} data-testid={`hero-slide-view-${s.slug}`}>View event</a>
            {s.rsvp && !s.cancelled && (
              <a className="ev-btn ev-btn-gold" href={s.rsvp.href} data-rsvp={s.rsvp.event_session_id} data-testid={`hero-slide-rsvp-${s.slug}`}>{s.rsvp.label} <span aria-hidden="true">→</span></a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
